import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, fmtMXN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Minus, ShoppingCart, Trash2, Pizza, ArrowLeft, Banknote, ArrowLeftRight, CreditCard, MapPin } from "lucide-react";
import MapPreview from "@/components/MapPreview";
import { getCurrentLocation } from "@/lib/geolocation";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function VariantesSelector({ producto, value, onChange }) {
  const setOpcion = (grupo, opcion) => {
    if (grupo.multiple) {
      const exists = value.find((v) => v.grupo === grupo.nombre && v.opcion === opcion.nombre);
      if (exists) onChange(value.filter((v) => !(v.grupo === grupo.nombre && v.opcion === opcion.nombre)));
      else onChange([...value, { grupo: grupo.nombre, opcion: opcion.nombre, precio_extra: opcion.precio_extra }]);
    } else {
      const filtered = value.filter((v) => v.grupo !== grupo.nombre);
      onChange([...filtered, { grupo: grupo.nombre, opcion: opcion.nombre, precio_extra: opcion.precio_extra }]);
    }
  };
  return (
    <div className="space-y-4">
      {(producto.variantes || []).map((g) => (
        <div key={g.id}>
          <div className="text-sm font-bold mb-2">{g.nombre}{g.obligatoria && <span className="text-destructive ml-1">*</span>}</div>
          <div className="grid grid-cols-2 gap-2">
            {g.opciones.map((o) => {
              const sel = value.find((v) => v.grupo === g.nombre && v.opcion === o.nombre);
              return (
                <button
                  type="button"
                  key={o.id || o.nombre}
                  onClick={() => setOpcion(g, o)}
                  className={`px-3 py-2 rounded-lg border-2 text-sm text-left transition-colors ${sel ? "border-primary bg-primary/10 font-bold" : "border-border bg-card hover:border-primary/50"}`}
                  data-testid={`variant-opt-${g.nombre}-${o.nombre}`}
                >
                  <div>{o.nombre}</div>
                  {o.precio_extra > 0 && <div className="text-xs text-muted-foreground">+{fmtMXN(o.precio_extra)}</div>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function NuevaOrden() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialMesa = searchParams.get("mesa");
  const telefonoOpcional = user?.role === "MESERO"; // MESERO: teléfono opcional en LLEVAR

  const [tipo, setTipo] = useState(initialMesa ? "MESA" : "MESA");
  const [mesaId, setMesaId] = useState(initialMesa || "");
  const [datos, setDatos] = useState({ cliente_nombre: "", cliente_telefono: "", cliente_direccion: "", referencias: "", entrega_tipo: "RECOGE", observaciones: "", metodo_pago: "EFECTIVO", monto_con_que_paga: "", latitud: null, longitud: null, ubicacion_capturada_en: null });

  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [mesas, setMesas] = useState([]);
  const [activeCat, setActiveCat] = useState("ALL");
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");

  // variant dialog
  const [variantOpen, setVariantOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [currentVariantes, setCurrentVariantes] = useState([]);
  const [currentCantidad, setCurrentCantidad] = useState(1);
  const [currentObs, setCurrentObs] = useState("");
  const [currentVariantesRapidas, setCurrentVariantesRapidas] = useState([]);
  const [nuevaVarianteTxt, setNuevaVarianteTxt] = useState("");
  const [incluirAcomp, setIncluirAcomp] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/productos"), api.get("/categorias"), api.get("/mesas")]).then(([p, c, m]) => {
      setProductos(p.data); setCategorias(c.data); setMesas(m.data);
    });
  }, []);

  const filteredProductos = useMemo(() => {
    let list = productos;
    if (activeCat !== "ALL") list = list.filter((p) => p.categoria_id === activeCat);
    if (search.trim()) {
      const t = search.toLowerCase();
      list = list.filter((p) => {
        if (p.nombre.toLowerCase().includes(t)) return true;
        const cat = categorias.find((c) => c.id === p.categoria_id);
        if (cat?.nombre.toLowerCase().includes(t)) return true;
        for (const g of p.variantes || []) {
          if (g.nombre.toLowerCase().includes(t)) return true;
          for (const o of g.opciones || []) if (o.nombre.toLowerCase().includes(t)) return true;
        }
        return false;
      });
    }
    return list;
  }, [productos, categorias, activeCat, search]);

  const addProducto = (prod) => {
    const tieneVariantes = (prod.variantes || []).length > 0;
    const tieneAcomp = !!prod.acompanamiento_id;
    if (tieneVariantes || tieneAcomp) {
      setCurrentProduct(prod);
      // preselect obligatorias con primera opción
      const pre = [];
      (prod.variantes || []).forEach((g) => {
        if (g.obligatoria && !g.multiple && g.opciones[0]) {
          pre.push({ grupo: g.nombre, opcion: g.opciones[0].nombre, precio_extra: g.opciones[0].precio_extra });
        }
      });
      setCurrentVariantes(pre);
      setCurrentCantidad(1);
      setCurrentObs("");
      setCurrentVariantesRapidas([]);
      setNuevaVarianteTxt("");
      setIncluirAcomp(true);
      setVariantOpen(true);
    } else {
      setItems([...items, { id: Math.random().toString(36), producto_id: prod.id, nombre: prod.nombre, precio_base: prod.precio_base, cantidad: 1, variantes: [], variantes_rapidas: [], observaciones: "" }]);
      toast.success(`Producto "${prod.nombre}" agregado a la orden.`);
    }
  };

  const confirmVariantes = () => {
    // Validar obligatorias
    const missing = (currentProduct.variantes || []).find((g) => g.obligatoria && !currentVariantes.some((v) => v.grupo === g.nombre));
    if (missing) return toast.error(`Falta seleccionar: ${missing.nombre}`);
    const tieneAcomp = !!currentProduct.acompanamiento_id;
    setItems([...items, {
      id: Math.random().toString(36),
      producto_id: currentProduct.id,
      nombre: currentProduct.nombre,
      precio_base: currentProduct.precio_base,
      cantidad: currentCantidad,
      variantes: currentVariantes,
      variantes_rapidas: currentVariantesRapidas,
      observaciones: currentObs,
      // Acompañamiento configurado: persistir la decisión del mesero
      acompanamiento_id: tieneAcomp ? currentProduct.acompanamiento_id : null,
      acompanamiento_nombre: tieneAcomp ? currentProduct.acompanamiento_nombre : null,
      acompanamiento_tiene_costo: tieneAcomp ? !!currentProduct.acompanamiento_tiene_costo : false,
      acompanamiento_costo: tieneAcomp ? (currentProduct.acompanamiento_costo || 0) : 0,
      incluir_acompanamiento: tieneAcomp ? !!incluirAcomp : null,
    }]);
    toast.success(`Producto "${currentProduct.nombre}" agregado a la orden.`);
    setVariantOpen(false);
  };

  const removeItem = (id) => {
    const it = items.find((i) => i.id === id);
    setItems(items.filter((i) => i.id !== id));
    if (it) toast.success(`Producto "${it.nombre}" eliminado de la orden.`);
  };
  const setCantidad = (id, delta) => setItems(items.map((i) => i.id === id ? { ...i, cantidad: Math.max(1, i.cantidad + delta) } : i));

  const subtotalItem = (it) => {
    const base = (it.precio_base + (it.variantes || []).reduce((s, v) => s + (v.precio_extra || 0), 0)) * it.cantidad;
    const acomp = (it.incluir_acompanamiento && it.acompanamiento_tiene_costo)
      ? (it.acompanamiento_costo || 0) * it.cantidad
      : 0;
    return base + acomp;
  };
  const total = items.reduce((s, it) => s + subtotalItem(it), 0);

  // Pago solicitado — solo para LLEVAR + DOMICILIO (mismo criterio que el flujo público).
  const requierePago = tipo === "LLEVAR" && datos.entrega_tipo === "DOMICILIO";
  const requiereMontoConCualPaga = requierePago && datos.metodo_pago === "EFECTIVO";
  const montoPaga = parseFloat(datos.monto_con_que_paga || "0");
  const cambioCalculado = requiereMontoConCualPaga && montoPaga >= total ? Math.round((montoPaga - total) * 100) / 100 : null;

  // Geolocalización opcional para LLEVAR + DOMICILIO. El backend ya acepta lat/lng.
  const usarGps = async () => {
    try {
      const { lat, lng } = await getCurrentLocation();
      setDatos((d) => ({
        ...d,
        latitud: lat,
        longitud: lng,
        ubicacion_capturada_en: new Date().toISOString(),
      }));
      toast.success("Ubicación capturada");
    } catch (err) {
      toast.error(err?.message || "No se pudo obtener la ubicación actual.");
    }
  };
  const onMoveMarker = ({ lat, lng }) => {
    setDatos((d) => ({
      ...d,
      latitud: lat,
      longitud: lng,
      ubicacion_capturada_en: new Date().toISOString(),
    }));
  };
  const limpiarUbicacion = () => {
    setDatos((d) => ({ ...d, latitud: null, longitud: null, ubicacion_capturada_en: null }));
  };

  const enviar = async () => {
    if (items.length === 0) return toast.error("Agrega al menos un producto");
    if (tipo === "MESA" && !mesaId) return toast.error("Selecciona una mesa");
    if (tipo === "LLEVAR") {
      if (!datos.cliente_nombre) return toast.error("Nombre requerido");
      if (!telefonoOpcional && !datos.cliente_telefono) return toast.error("Nombre y teléfono requeridos");
      if (datos.entrega_tipo === "DOMICILIO" && !datos.cliente_direccion) return toast.error("Dirección requerida");
      if (requierePago) {
        if (!datos.metodo_pago) return toast.error("Selecciona un método de pago");
        if (requiereMontoConCualPaga) {
          if (!datos.monto_con_que_paga || Number.isNaN(montoPaga)) return toast.error("Indica con cuánto va a pagar");
          if (montoPaga < total) return toast.error(`El monto debe ser mayor o igual al total (${fmtMXN(total)})`);
        }
      }
    }
    try {
      const payload = {
        tipo,
        mesa_id: tipo === "MESA" ? mesaId : null,
        items: items.map((it) => ({
          producto_id: it.producto_id,
          cantidad: it.cantidad,
          variantes: it.variantes,
          variantes_rapidas: it.variantes_rapidas || [],
          observaciones: it.observaciones,
          incluir_acompanamiento: it.acompanamiento_id ? !!it.incluir_acompanamiento : null,
        })),
        ...datos,
        direccion_origen: datos.entrega_tipo === "DOMICILIO"
          ? (datos.latitud != null && datos.longitud != null ? "GPS" : "MANUAL")
          : null,
        // Geolocalización opcional; backend ignora si no aplica.
        latitud: datos.latitud,
        longitud: datos.longitud,
        ubicacion_capturada_en: datos.ubicacion_capturada_en,
        // Pago solicitado — backend solo lo guarda si tipo=LLEVAR + DOMICILIO.
        metodo_pago_solicitado: requierePago ? datos.metodo_pago : null,
        monto_con_que_paga: requiereMontoConCualPaga ? montoPaga : null,
      };
      const r = await api.post("/ordenes", payload);
      toast.success(`Orden ${r.data.numero} creada`);
      navigate(`/mesero/orden/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft className="h-5 w-5" /></Button>
          <div>
            <h2 className="text-3xl font-black tracking-tight">Nueva orden</h2>
            <p className="text-sm text-muted-foreground mt-1">Selecciona productos y configura la entrega.</p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardContent className="p-4">
              <Tabs value={tipo} onValueChange={setTipo}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="MESA" data-testid="tipo-mesa-tab">Mesa</TabsTrigger>
                  <TabsTrigger value="LLEVAR" data-testid="tipo-llevar-tab">Para llevar</TabsTrigger>
                </TabsList>
                <TabsContent value="MESA" className="space-y-2 mt-3">
                  <Label>Mesa *</Label>
                  <Select value={mesaId} onValueChange={setMesaId}>
                    <SelectTrigger data-testid="mesa-select"><SelectValue placeholder="Selecciona mesa" /></SelectTrigger>
                    <SelectContent>{mesas.map((m) => <SelectItem key={m.id} value={m.id}>{m.nombre} ({m.capacidad}p)</SelectItem>)}</SelectContent>
                  </Select>
                </TabsContent>
                <TabsContent value="LLEVAR" className="space-y-3 mt-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label>Nombre *</Label><Input value={datos.cliente_nombre} onChange={(e) => setDatos({ ...datos, cliente_nombre: e.target.value })} data-testid="cliente-nombre-input" /></div>
                    <div><Label>Teléfono{telefonoOpcional ? "" : " *"}</Label><Input value={datos.cliente_telefono} onChange={(e) => setDatos({ ...datos, cliente_telefono: e.target.value })} data-testid="cliente-telefono-input" /></div>
                  </div>
                  <div>
                    <Label>Tipo de entrega</Label>
                    <Tabs value={datos.entrega_tipo} onValueChange={(v) => setDatos({ ...datos, entrega_tipo: v })}>
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="RECOGE">Recoge en sucursal</TabsTrigger>
                        <TabsTrigger value="DOMICILIO">Entrega a domicilio</TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                  {datos.entrega_tipo === "DOMICILIO" && (
                    <>
                      <div><Label>Dirección *</Label><Textarea value={datos.cliente_direccion} onChange={(e) => setDatos({ ...datos, cliente_direccion: e.target.value })} data-testid="cliente-direccion-input" /></div>
                      <div><Label>Referencias</Label><Input value={datos.referencias} onChange={(e) => setDatos({ ...datos, referencias: e.target.value })} /></div>

                      {/* Ubicación opcional. El mesero puede dejar solo dirección escrita. */}
                      <div className="space-y-2 pt-2 border-t border-border">
                        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Ubicación (opcional)</Label>
                        <Button type="button" variant="outline" className="w-full" onClick={usarGps} data-testid="mesero-gps-btn">
                          <MapPin className="h-4 w-4 mr-1" />
                          {datos.latitud ? "Recentrar ubicación" : "Compartir ubicación actual"}
                        </Button>
                        {datos.latitud != null && datos.longitud != null && (
                          <div className="space-y-2">
                            <MapPreview lat={datos.latitud} lng={datos.longitud} onChange={onMoveMarker} height={200} />
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                                <MapPin className="h-3 w-3" /> Marcador arrastrable
                              </span>
                              <button type="button" onClick={limpiarUbicacion} className="text-destructive hover:underline" data-testid="mesero-gps-clear">Quitar ubicación</button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Método de pago — mismo patrón que el flujo público. Solo se solicita para LLEVAR a domicilio. */}
                      <div className="space-y-2 pt-2 border-t border-border">
                        <Label>Método de pago *</Label>
                        <div className="grid grid-cols-3 gap-2" data-testid="metodo-pago-grid">
                          {[
                            { v: "EFECTIVO", label: "Efectivo", icon: Banknote },
                            { v: "TRANSFERENCIA", label: "Transferencia", icon: ArrowLeftRight },
                            { v: "TARJETA", label: "Tarjeta", icon: CreditCard },
                          ].map(({ v, label, icon: Icon }) => {
                            const active = datos.metodo_pago === v;
                            return (
                              <button
                                key={v}
                                type="button"
                                onClick={() => setDatos({ ...datos, metodo_pago: v, monto_con_que_paga: v === "EFECTIVO" ? datos.monto_con_que_paga : "" })}
                                className={`flex flex-col items-center gap-1 rounded-lg border-2 px-2 py-3 text-xs font-bold transition-colors ${active ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50"}`}
                                data-testid={`metodo-pago-${v.toLowerCase()}`}
                              >
                                <Icon className="h-5 w-5" />
                                <span>{label}</span>
                              </button>
                            );
                          })}
                        </div>
                        {requiereMontoConCualPaga && (
                          <div className="space-y-1 mt-2 rounded-lg bg-secondary/40 p-3 border border-border" data-testid="monto-paga-container">
                            <Label>¿Con cuánto va a pagar? *</Label>
                            <Input
                              type="number"
                              inputMode="decimal"
                              min={total}
                              step="0.01"
                              placeholder={fmtMXN(total)}
                              value={datos.monto_con_que_paga}
                              onChange={(e) => setDatos({ ...datos, monto_con_que_paga: e.target.value })}
                              data-testid="monto-paga-input"
                            />
                            <div className="text-xs text-muted-foreground">
                              El repartidor llevará el cambio. Mínimo: <span className="font-bold">{fmtMXN(total)}</span>
                            </div>
                            {cambioCalculado !== null && (
                              <div className="text-sm" data-testid="cambio-preview">
                                Cambio: <span className="font-black text-emerald-600 dark:text-emerald-400">{fmtMXN(cambioCalculado)}</span>
                              </div>
                            )}
                            {datos.monto_con_que_paga && montoPaga < total && (
                              <div className="text-xs text-destructive">El monto debe ser mayor o igual al total</div>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </TabsContent>
              </Tabs>
              <div className="mt-3"><Label>Observaciones</Label><Textarea value={datos.observaciones} onChange={(e) => setDatos({ ...datos, observaciones: e.target.value })} placeholder="Notas especiales…" /></div>
            </CardContent>
          </Card>

          <div className="flex gap-2 flex-wrap items-center">
            <div className="relative flex-1 min-w-[200px]">
              <ArrowLeft className="hidden" />
              <input
                type="text"
                placeholder="Buscar producto, categoría o variante…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full h-11 px-4 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="nueva-orden-search"
              />
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant={activeCat === "ALL" ? "default" : "outline"} onClick={() => setActiveCat("ALL")} data-testid="cat-all-btn">Todos</Button>
            {categorias.map((c) => (
              <Button key={c.id} size="sm" variant={activeCat === c.id ? "default" : "outline"} onClick={() => setActiveCat(c.id)} data-testid={`cat-btn-${c.id}`}>{c.nombre}</Button>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filteredProductos.map((p) => {
              let precioMin = p.precio_base || 0;
              const tipoT = (p.variantes || []).find((g) => g.obligatoria);
              if (tipoT && tipoT.opciones?.length) {
                const minExtra = Math.min(...tipoT.opciones.map((o) => o.precio_extra || 0));
                precioMin = (p.precio_base || 0) + minExtra;
              }
              return (
                <Card key={p.id} className="cursor-pointer hover:border-primary transition-colors" onClick={() => addProducto(p)} data-testid={`add-producto-${p.id}`}>
                  <CardContent className="p-3">
                    <div className="aspect-video rounded-lg bg-secondary mb-2 overflow-hidden flex items-center justify-center">
                      {p.imagen_url ? <img src={p.imagen_url.startsWith("http") ? p.imagen_url : `${API}${p.imagen_url}`} alt={p.nombre} className="w-full h-full object-cover" /> : <Pizza className="h-10 w-10 text-muted-foreground" />}
                    </div>
                    <div className="font-bold text-sm leading-tight line-clamp-2" title={p.nombre}>{p.nombre}</div>
                    {p.acompanamiento_nombre && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200" data-testid={`add-prod-acomp-${p.id}`}>
                        🍟 + {p.acompanamiento_nombre}
                        {p.acompanamiento_tiene_costo ? ` (+${fmtMXN(p.acompanamiento_costo || 0)})` : ""}
                      </div>
                    )}
                    <div className="flex justify-between mt-1">
                      <div className="text-xs text-muted-foreground">{p.area}</div>
                      <div className="font-black text-primary">
                        {(p.variantes || []).some((g) => g.obligatoria) ? `desde ${fmtMXN(precioMin)}` : fmtMXN(precioMin)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        <div className="lg:sticky lg:top-20 h-fit">
          <Card>
            <CardContent className="p-5">
              <h3 className="text-lg font-bold mb-3 flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> Resumen ({items.length})</h3>
              {items.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">Sin productos.</div>}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {items.map((it) => (
                  <div key={it.id} className="border-b border-border pb-2 last:border-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="font-bold">{it.nombre}</div>
                        {it.variantes.length > 0 && <div className="text-xs text-muted-foreground">{it.variantes.map((v) => v.opcion).join(" · ")}</div>}
                        {it.observaciones && <div className="text-xs text-orange-500 italic">📝 {it.observaciones}</div>}
                        {it.acompanamiento_id && it.incluir_acompanamiento && (
                          <div className="text-[11px] mt-0.5 flex items-center gap-1 flex-wrap" data-testid={`cart-acomp-${it.id}`}>
                            <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-200 dark:bg-amber-900/60 text-amber-900 dark:text-amber-100">🍟 PORCIÓN</span>
                            <span className="font-semibold text-amber-700 dark:text-amber-300">{it.acompanamiento_nombre}</span>
                            <span className={it.acompanamiento_tiene_costo ? "text-primary font-bold" : "text-emerald-600 dark:text-emerald-400 font-bold"}>
                              {it.acompanamiento_tiene_costo ? `+${fmtMXN(it.acompanamiento_costo)}` : "Gratis"}
                            </span>
                          </div>
                        )}
                        {it.acompanamiento_id && !it.incluir_acompanamiento && (
                          <div className="text-[11px] mt-0.5 text-muted-foreground italic" data-testid={`cart-acomp-skipped-${it.id}`}>
                            Sin {it.acompanamiento_nombre}
                          </div>
                        )}
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => removeItem(it.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center gap-1">
                        <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setCantidad(it.id, -1)}><Minus className="h-3 w-3" /></Button>
                        <span className="font-bold w-6 text-center">{it.cantidad}</span>
                        <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setCantidad(it.id, 1)}><Plus className="h-3 w-3" /></Button>
                      </div>
                      <div className="font-bold">{fmtMXN(subtotalItem(it))}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex justify-between font-black text-2xl">
                  <span>Total</span><span className="text-primary">{fmtMXN(total)}</span>
                </div>
                <Button className="w-full h-14 mt-4 text-base font-black" onClick={enviar} disabled={items.length === 0} data-testid="enviar-orden-btn">
                  Enviar a cocina
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={variantOpen} onOpenChange={setVariantOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{currentProduct?.nombre}</DialogTitle></DialogHeader>
          {currentProduct && (
            <>
              <VariantesSelector producto={currentProduct} value={currentVariantes} onChange={setCurrentVariantes} />

              {/* Complemento / acompañamiento configurado para el producto */}
              {currentProduct.acompanamiento_id && currentProduct.acompanamiento_nombre && (
                <div className="border-t border-border pt-3 mt-3" data-testid="nuevaorden-acomp-section">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Complemento</Label>
                  <label
                    className={`mt-2 flex items-center justify-between gap-3 rounded-lg border-2 p-3 cursor-pointer transition-colors ${incluirAcomp ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}
                    data-testid="nuevaorden-acomp-toggle"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={incluirAcomp}
                        onChange={(e) => setIncluirAcomp(e.target.checked)}
                        data-testid="nuevaorden-acomp-checkbox"
                      />
                      <div className="min-w-0">
                        <div className="font-bold text-sm flex items-center gap-1">
                          🍟 {currentProduct.acompanamiento_nombre}
                        </div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
                          {currentProduct.acompanamiento_tiene_costo ? "Costo adicional" : "Incluido sin costo"}
                        </div>
                      </div>
                    </div>
                    <div className={`text-sm font-black ${currentProduct.acompanamiento_tiene_costo ? "text-primary" : "text-emerald-600 dark:text-emerald-400"}`}>
                      {currentProduct.acompanamiento_tiene_costo
                        ? `+${fmtMXN(currentProduct.acompanamiento_costo || 0)}`
                        : "Gratis"}
                    </div>
                  </label>
                  {!incluirAcomp && (
                    <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">No se enviará a {currentProduct.acompanamiento_area || "su área"} ni sumará al total.</p>
                  )}
                </div>
              )}

              {/* Variantes rápidas */}
              <div className="border-t border-border pt-3 mt-3">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Opción rápida (no está en el catálogo)</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={nuevaVarianteTxt}
                    onChange={(e) => setNuevaVarianteTxt(e.target.value)}
                    placeholder="Ej. Sin cilantro, picante…"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (nuevaVarianteTxt.trim()) { setCurrentVariantesRapidas([...currentVariantesRapidas, nuevaVarianteTxt.trim()]); setNuevaVarianteTxt(""); } } }}
                    data-testid="nuevaorden-variante-rapida-input"
                  />
                  <Button type="button" variant="outline" onClick={() => { if (nuevaVarianteTxt.trim()) { setCurrentVariantesRapidas([...currentVariantesRapidas, nuevaVarianteTxt.trim()]); setNuevaVarianteTxt(""); } }}>Agregar</Button>
                </div>
                {currentVariantesRapidas.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {currentVariantesRapidas.map((t, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 rounded-md text-xs font-semibold">
                        {t}
                        <button type="button" onClick={() => setCurrentVariantesRapidas(currentVariantesRapidas.filter((_, idx) => idx !== i))}>×</button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground mt-2">⚠ Se registra en auditoría.</div>
              </div>

              <div className="mt-3">
                <Label>Observaciones</Label>
                <Textarea value={currentObs} onChange={(e) => setCurrentObs(e.target.value)} placeholder="Sin cebolla, etc." />
              </div>
              <div className="flex items-center justify-between mt-3">
                <Label>Cantidad</Label>
                <div className="flex items-center gap-2">
                  <Button size="icon" variant="outline" onClick={() => setCurrentCantidad((c) => Math.max(1, c - 1))}><Minus className="h-4 w-4" /></Button>
                  <span className="text-2xl font-black w-10 text-center">{currentCantidad}</span>
                  <Button size="icon" variant="outline" onClick={() => setCurrentCantidad((c) => c + 1)}><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVariantOpen(false)}>Cancelar</Button>
            <Button onClick={confirmVariantes} data-testid="confirm-variantes-btn">Agregar al pedido</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
